import React, { useRef, useState } from 'react';
import { Bot, Send, Sparkles, X } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';

const MemberAIChat = ({ compact = false, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef(null);

  const send = async (event) => {
    event.preventDefault();
    const message = text.trim();
    if (!message || busy) return;
    const history = messages.map(({ role, content }) => ({ role, content }));
    setMessages((current) => [...current, { role: 'user', content: message }]);
    setText('');
    setBusy(true);
    setError('');
    try {
      const { data } = await api.post('/member-center/ai-chat', { message, history: history.slice(-12) });
      setMessages((current) => [...current, { role: 'assistant', content: data.reply }]);
      requestAnimationFrame(() => endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }));
    } catch (requestError) {
      setError(formatApiError(requestError, 'AI-chatten kunne ikkje svare akkurat no.'));
      setMessages((current) => current.slice(0, -1));
      setText(message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${compact ? 'flex h-full flex-col' : ''} overflow-hidden rounded-xl border border-white/10 bg-[#0c0a0e]`} data-testid="member-ai-chat">
      <header className="flex items-center justify-between gap-3 border-b border-white/10 px-5 py-4">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-400/15 text-emerald-300"><Bot size={21} /></span>
          <div className="min-w-0"><h1 className="text-lg font-bold">Refuel-assistenten</h1><p className="text-xs text-white/45">AI-chat for medlemmer</p></div>
        </div>
        {compact && <button type="button" onClick={onClose} aria-label="Lukk AI-chat" title="Lukk chat" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-white/50 transition hover:bg-white/10 hover:text-white"><X size={18} /></button>}
      </header>
      <div className={`${compact ? 'min-h-0 flex-1' : 'min-h-[350px] max-h-[60vh]'} space-y-4 overflow-y-auto p-4 md:p-6`} aria-live="polite">
        {messages.length === 0 && (
          <div className={`flex ${compact ? 'min-h-[220px]' : 'min-h-[300px]'} flex-col items-center justify-center text-center`}>
            <Sparkles size={24} className="text-emerald-300" />
            <p className="mt-4 text-sm font-semibold">Kva lurer du på?</p>
            <p className="mt-1 max-w-sm text-sm text-white/45">Spør om Refuel Weekend eller start ein vanleg samtale.</p>
          </div>
        )}
        {messages.map((item, index) => (
          <article key={`${item.role}-${index}`} className={`max-w-[85%] whitespace-pre-wrap break-words rounded-xl px-4 py-3 text-sm leading-6 ${item.role === 'user' ? 'ml-auto bg-[#ff2e2e]/15 text-white' : 'bg-white/[0.06] text-white/80'}`}>
            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-white/40">{item.role === 'user' ? 'Du' : 'Refuel AI'}</p>
            {item.content}
          </article>
        ))}
        {busy && <p className="text-sm text-white/45" role="status">Refuel-assistenten skriv…</p>}
        <div ref={endRef} />
      </div>
      {error && <p role="alert" className="border-t border-red-500/20 bg-red-500/10 px-4 py-2 text-sm text-red-200">{error}</p>}
      <form onSubmit={send} className="border-t border-white/10 p-3 md:p-4">
        <div className="flex items-end gap-2 rounded-xl border border-white/10 bg-white/[0.035] p-2 focus-within:border-emerald-300/40">
          <label htmlFor="member-ai-input" className="sr-only">Skriv til Refuel-assistenten</label>
          <textarea id="member-ai-input" data-testid="member-ai-input" rows={1} maxLength={2000} value={text} onChange={(event) => setText(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); event.currentTarget.form?.requestSubmit(); } }} disabled={busy} placeholder="Skriv ei melding…" className="max-h-32 min-h-10 flex-1 resize-y bg-transparent px-2 py-2 text-sm text-white outline-none placeholder:text-white/30 disabled:opacity-50" />
          <button type="submit" data-testid="member-ai-send" disabled={busy || !text.trim()} title="Send melding" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-emerald-300 text-black transition hover:bg-emerald-200 disabled:cursor-not-allowed disabled:opacity-40"><Send size={17} /></button>
        </div>
      </form>
    </div>
  );
};

export default MemberAIChat;